import Logger from "../../../services/Logger";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import UtilService from "../../../services/UtilService";
import AccountService from "../AccountService";

const AllSubs = ({ userId }) => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [subscriptionsPagination, setSubscriptionsPagination] = useState({
        page: 0,
        pageSize: 10,
        totalElements: 0,
        totalPages: 0
    });
    const [subscriptionsSort] = useState([{ field: "createdAt", sort: "desc" }]);


    // Fetch subscriptions with pagination
    useEffect(() => {
        const fetchSubscriptions = async () => {
            if (!userId) return;

            try {
                const subscriptionData = await AccountService.getUserSubscriptions(
                    userId,
                    subscriptionsPagination.page,
                    subscriptionsPagination.pageSize,
                    subscriptionsSort
                );

                Logger.log("Fetched subscriptionData:", subscriptionData);

                if (subscriptionData) {
                    setSubscriptions(subscriptionData.content || []);
                    setSubscriptionsPagination(prev => ({
                        ...prev,
                        totalElements: subscriptionData.totalElements ?? 0,
                        totalPages: subscriptionData.totalPages ?? 0,
                        page: subscriptionData.number ?? prev.page
                    }));
                } else {
                    setSubscriptions([]);
                }
            } catch (err) {
                Logger.error("Error fetching subscriptions:", err);
                setSubscriptions([]);
            }
        };

        fetchSubscriptions();
    }, [userId, subscriptionsPagination.page, subscriptionsPagination.pageSize, subscriptionsSort]);

    const handleSubscriptionsPageChange = (_event, newPage) => {
        setSubscriptionsPagination(prev => ({
            ...prev,
            page: newPage - 1 // MUI Pagination uses 1-based indexing
        }));
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    All Subscriptions
                </Typography>
                {subscriptions.length === 0 ? (
                    <Typography variant="body1" color="text.secondary">
                        No subscriptions found for this user.
                    </Typography>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User Email</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>End Date</TableCell>
                                    <TableCell>Plan ID</TableCell>
                                    <TableCell>Stripe Subscription ID</TableCell>
                                    <TableCell>Canceled At</TableCell>
                                    <TableCell>Created At</TableCell>
                                    <TableCell>Updated At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {subscriptions.map((subscription) => (
                                    <TableRow key={subscription.id}>
                                        <TableCell>{subscription.userEmail || "N/A"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={subscription.status || "N/A"}
                                                color={
                                                    subscription.status === "ACTIVE"
                                                        ? "success"
                                                        : subscription.status === "INACTIVE" ||
                                                            subscription.status === "CANCELLED" ||
                                                            subscription.status === "CANCELED"
                                                            ? "error"
                                                            : "default"
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {UtilService.formatDate(subscription.startAt) || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                            {UtilService.formatDate(subscription.endAt) || "N/A"}
                                        </TableCell>
                                        <TableCell>{subscription.planId || "N/A"}</TableCell>
                                        <TableCell>
                                            {subscription.stripeSubscriptionId ? (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.75rem',
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={subscription.stripeSubscriptionId}
                                                >
                                                    {subscription.stripeSubscriptionId}
                                                </Typography>
                                            ) : (
                                                "N/A"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {subscription.canceledAt
                                                ? UtilService.formatDate(subscription.canceledAt)
                                                : "N/A"}
                                        </TableCell>
                                        <TableCell>
                                            {UtilService.formatDate(subscription.createdAt) || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                            {UtilService.formatDate(subscription.updatedAt) || "N/A"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                
                {/* Pagination */}
                {subscriptionsPagination.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Stack spacing={2} alignItems="center">
                            <Pagination
                                count={subscriptionsPagination.totalPages}
                                page={subscriptionsPagination.page + 1} // Convert 0-based to 1-based for MUI
                                onChange={handleSubscriptionsPageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                            <Typography variant="body2" color="text.secondary">
                                Showing {subscriptions.length} of {subscriptionsPagination.totalElements} subscriptions
                            </Typography>
                        </Stack>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default AllSubs;

